export interface LintOptions {
  /**
   * Iterative depth guard, applied to the token stream BEFORE `parseTree` runs.
   *
   * Not decorative: `parseTree` and `visit` were measured to throw `RangeError` at depth
   * 3928, and a Worker's stack can be smaller than the main thread's (ADR-0007, ADR-0010).
   * 500 leaves a wide margin.
   */
  readonly maxDepth: number;

  /** Diagnostics past this count are dropped and `LintReport.truncated` is set. */
  readonly maxDiagnostics: number;

  /**
   * Hard size guard, in UTF-8 bytes, applied by `analyzeDocument` BEFORE a single token is
   * produced. A document past it is REFUSED with an `internal/analysis-aborted` diagnostic
   * — never with a throw, and never by starting work that would hang the tab.
   *
   * ADDED 2026-09-14 by the core-integration stage; `docs/ARCHITECTURE.md` §4.3 declared
   * `LintOptions` with four fields. The reason it has to live here rather than in
   * `src/app/config.ts`, where the size ladder otherwise lives, is the layer rule: the guard
   * has to run inside the engine (a Worker calls `analyzeDocument` directly, so a check in
   * the UI would not protect it), and `src/core/**` may not import `@/app/*` — gate 2 makes
   * that a lint error, not a convention. So the number lives here and `config.ts` names it:
   * `HARD_LIMIT_BYTES = DEFAULT_LINT_OPTIONS.maxBytes`. There is still exactly one 10 MiB in
   * the repository.
   *
   * It is an option rather than a module constant so a caller can lower it (a Worker with a
   * smaller budget) and so the refusal path is testable without allocating ten megabytes.
   */
  readonly maxBytes: number;

  readonly detectDuplicateKeys: boolean;
  readonly detectPrecisionLoss: boolean;
}

export const DEFAULT_LINT_OPTIONS: LintOptions = {
  maxDepth: 500,
  maxDiagnostics: 500,
  maxBytes: 10 * 1024 * 1024,
  detectDuplicateKeys: true,
  detectPrecisionLoss: true,
};
